/**
 * Checkout return after email verification / post-auth redirect.
 *
 * Guest registers on checkout but must verify email before SLAS login. The verification
 * link often opens a new tab, so we use a short-lived cookie (not sessionStorage).
 *
 * Cookie `pendingCheckoutReturn=1` is a flag only — not a full URL. On consume,
 * callers use navigate('/checkout') so the active locale/site prefix is applied.
 *
 * Set: use-registration-submit (verification required), social-login (OAuth from checkout).
 * Consume + redirect: login page, sso-callback, PendingCheckoutReturn (checkout only if
 * basket has at least one line item; otherwise cart).
 * Consume only (clear flag): use-login-submit when context is checkout (already on page).
 */

export const CHECKOUT_RETURN_COOKIE = 'pendingCheckoutReturn'
const CHECKOUT_RETURN_FLAG = '1'
const MAX_AGE_SECONDS = 30 * 60

/**
 * Checkout step only (not confirmation) — where we send the shopper back after auth.
 * @param {string} pathname
 * @returns {boolean}
 */
export const isCheckoutReturnPath = (pathname = '') => {
    const path = String(pathname).split('?')[0].replace(/\/+$/, '') || '/'
    return /\/checkout$/i.test(path) || /\/esw-checkout$/i.test(path)
}

const readCookieValue = (name) => {
    if (typeof document === 'undefined') return null
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
    return match ? decodeURIComponent(match[1]) : null
}

export const clearCheckoutReturn = () => {
    if (typeof document === 'undefined') return
    document.cookie = `${CHECKOUT_RETURN_COOKIE}=; path=/; max-age=0; samesite=lax`
}

/** @param {object|null|undefined} basket */
export const basketHasCheckoutLineItems = (basket) => (basket?.productItems?.length ?? 0) > 0

/**
 * `useCustomerBaskets` / `refetch()` returns `{baskets, total}`. `useCurrentBasket().data` is one basket.
 * Prefer a basket that already has line items so an empty sibling does not win.
 * @param {object|null|undefined} data
 */
export const basketFromCustomerBaskets = (data) => {
    if (!data) return null
    if (Array.isArray(data.baskets)) {
        return (
            data.baskets.find((basket) => basketHasCheckoutLineItems(basket)) ||
            data.baskets[0] ||
            null
        )
    }
    if (data.productItems || data.basketId) return data
    return null
}

export const hasPendingCheckoutReturn = () => {
    if (typeof document === 'undefined') return false
    return readCookieValue(CHECKOUT_RETURN_COOKIE) === CHECKOUT_RETURN_FLAG
}

/**
 * When the pending flag is set: clears it and picks checkout vs cart from line-item count.
 * @param {object|null|undefined} basket
 * @returns {'/checkout'|'/cart'|null} null when no flag was set
 */
export const resolveCheckoutReturnNavigation = (basket) => {
    if (typeof window === 'undefined') return null
    const stored = readCookieValue(CHECKOUT_RETURN_COOKIE)
    if (stored !== CHECKOUT_RETURN_FLAG) return null
    clearCheckoutReturn()
    return basketHasCheckoutLineItems(basket) ? '/checkout' : '/cart'
}

/**
 * Sets the pending checkout return flag when auth started on a checkout URL.
 * No-op if pathname is not a checkout step (see isCheckoutReturnPath).
 * @param {string} [pathname] - Defaults to window.location.pathname.
 */
export const persistCheckoutReturn = (pathname) => {
    if (typeof window === 'undefined') return
    const path =
        String(pathname ?? window.location.pathname)
            .split('?')[0]
            .replace(/\/+$/, '') || '/'
    if (!isCheckoutReturnPath(path)) return

    const secure = window.location.protocol === 'https:' ? '; secure' : ''
    document.cookie = `${CHECKOUT_RETURN_COOKIE}=${CHECKOUT_RETURN_FLAG}; path=/; max-age=${MAX_AGE_SECONDS}; samesite=lax${secure}`
}

/**
 * Reads and clears the flag; returns a route for useNavigation (locale applied by the router).
 * @returns {'/checkout'|null}
 */
export const consumeCheckoutReturnRoute = () => {
    if (typeof window === 'undefined') return null

    const stored = readCookieValue(CHECKOUT_RETURN_COOKIE)
    clearCheckoutReturn()
    if (stored === CHECKOUT_RETURN_FLAG) return '/checkout'
    return null
}

/** Alias for consumeCheckoutReturnRoute (e.g. use-login-submit checkout context cleanup). */
export const consumeCheckoutReturn = consumeCheckoutReturnRoute
